import { useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Globe, Sparkles, Loader2, Check, X, Pencil, ArrowLeft } from "lucide-react";

export function CloneMenuDialog({ open, onOpenChange, onRefresh }) {
  const { authAxios } = useAuth();
  const [step, setStep] = useState("url"); // "url" | "preview" | "importing"
  const [url, setUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [editIdx, setEditIdx] = useState(null);
  const [importing, setImporting] = useState(false);

  const selectedCount = items.filter(i => i.selected).length;

  const handlePreview = async () => {
    if (!url.trim()) return;
    setLoading(true);
    try {
      const res = await authAxios.post("/menu/clone-preview", { url }, { timeout: 180000 });
      setItems(res.data.items || []);
      setCategories(res.data.categories || []);
      setStep("preview");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao extrair menu");
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = async () => {
    const selected = items.filter(i => i.selected);
    if (selected.length === 0) {
      toast.error("Selecione pelo menos 1 item");
      return;
    }
    setImporting(true);
    try {
      const res = await authAxios.post("/menu/clone-confirm", { items: selected }, { timeout: 30000 });
      toast.success(`${res.data.products_created} produtos importados!`);
      handleClose();
      onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao importar");
    } finally {
      setImporting(false);
    }
  };

  const toggleAll = (checked) => {
    setItems(prev => prev.map(i => ({ ...i, selected: checked })));
  };

  const toggleItem = (idx) => {
    setItems(prev => prev.map((item, i) => i === idx ? { ...item, selected: !item.selected } : item));
  };

  const updateItem = (idx, field, value) => {
    setItems(prev => prev.map((item, i) => i === idx ? { ...item, [field]: value } : item));
  };

  const toggleCategory = (cat, checked) => {
    setItems(prev => prev.map(i => i.category === cat ? { ...i, selected: checked } : i));
  };

  const handleClose = () => {
    setStep("url");
    setUrl("");
    setItems([]);
    setCategories([]);
    setEditIdx(null);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v ? handleClose() : onOpenChange(v)}>
      <DialogContent className="max-w-2xl max-h-[85vh] flex flex-col p-0 gap-0" data-testid="clone-menu-dialog">
        <DialogHeader className="px-6 pt-5 pb-3 border-b border-gray-100">
          <DialogTitle className="flex items-center gap-2 text-base font-bold uppercase">
            <Globe className="w-5 h-5 text-blue-600" />
            {step === "url" ? "Clonar Menu de URL" : "Preview - Revisar Itens"}
          </DialogTitle>
        </DialogHeader>

        {step === "url" && (
          <div className="px-6 py-5 space-y-4">
            <p className="text-sm text-gray-500">
              Cole o link do cardapio de outro restaurante. A IA vai extrair todos os itens para voce revisar antes de importar.
            </p>
            <Input
              placeholder="https://restaurante.com/menu"
              value={url}
              onChange={e => setUrl(e.target.value)}
              data-testid="clone-url-input"
              onKeyDown={e => e.key === "Enter" && handlePreview()}
            />
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={handleClose}>Cancelar</Button>
              <Button
                disabled={!url.trim() || loading}
                className="bg-blue-600 hover:bg-blue-700 text-white gap-1.5"
                data-testid="clone-preview-btn"
                onClick={handlePreview}
              >
                {loading ? (
                  <><Loader2 className="w-4 h-4 animate-spin" /> Extraindo... (pode levar ~1 min)</>
                ) : (
                  <><Sparkles className="w-4 h-4" /> Extrair Menu</>
                )}
              </Button>
            </div>
          </div>
        )}

        {step === "preview" && (
          <>
            <div className="px-6 py-3 bg-gray-50 border-b border-gray-100 flex items-center justify-between flex-wrap gap-2">
              <div className="flex items-center gap-3">
                <Button variant="ghost" size="sm" className="gap-1 text-gray-500 h-8" onClick={() => setStep("url")} data-testid="clone-back-btn">
                  <ArrowLeft className="w-3.5 h-3.5" /> Voltar
                </Button>
                <span className="text-sm text-gray-600">
                  <span className="font-bold text-blue-700">{selectedCount}</span> de {items.length} selecionados
                </span>
              </div>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" className="h-8 text-xs" onClick={() => toggleAll(true)} data-testid="clone-select-all">
                  Selecionar todos
                </Button>
                <Button variant="outline" size="sm" className="h-8 text-xs" onClick={() => toggleAll(false)} data-testid="clone-deselect-all">
                  Limpar
                </Button>
              </div>
            </div>

            {/* Category filter chips */}
            <div className="px-6 py-2 flex gap-1.5 flex-wrap border-b border-gray-50">
              {categories.map(cat => {
                const catCount = items.filter(i => i.category === cat).length;
                const catSelected = items.filter(i => i.category === cat && i.selected).length;
                const allSelected = catCount === catSelected;
                return (
                  <button
                    key={cat}
                    className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium border transition ${
                      allSelected ? "bg-blue-50 border-blue-200 text-blue-700" : "bg-gray-50 border-gray-200 text-gray-500 hover:border-gray-300"
                    }`}
                    onClick={() => toggleCategory(cat, !allSelected)}
                    data-testid={`clone-cat-${cat.replace(/\s+/g, "-").toLowerCase()}`}
                  >
                    {cat} <Badge variant="secondary" className="ml-0.5 h-4 px-1 text-[10px]">{catSelected}/{catCount}</Badge>
                  </button>
                );
              })}
            </div>

            {/* Items list */}
            <ScrollArea className="flex-1 px-6 py-2" data-testid="clone-preview-list">
              <div className="space-y-1">
                {items.map((item, idx) => (
                  <div
                    key={item.temp_id}
                    className={`flex items-center gap-3 p-2.5 rounded-lg border transition ${
                      item.selected ? "border-blue-100 bg-white" : "border-gray-100 bg-gray-50 opacity-60"
                    }`}
                    data-testid={`clone-item-${idx}`}
                  >
                    <Checkbox
                      checked={item.selected}
                      onCheckedChange={() => toggleItem(idx)}
                      data-testid={`clone-check-${idx}`}
                    />

                    {editIdx === idx ? (
                      <div className="flex-1 grid grid-cols-[1fr_80px] gap-2">
                        <Input
                          value={item.name}
                          onChange={e => updateItem(idx, "name", e.target.value)}
                          className="h-8 text-sm"
                          data-testid={`clone-edit-name-${idx}`}
                        />
                        <Input
                          type="number"
                          step="0.01"
                          value={item.price}
                          onChange={e => updateItem(idx, "price", parseFloat(e.target.value) || 0)}
                          className="h-8 text-sm"
                          data-testid={`clone-edit-price-${idx}`}
                        />
                        <Input
                          value={item.description}
                          onChange={e => updateItem(idx, "description", e.target.value)}
                          placeholder="Descricao (opcional)"
                          className="h-8 text-sm col-span-2"
                          data-testid={`clone-edit-desc-${idx}`}
                        />
                        <Button variant="ghost" size="sm" className="h-7 text-xs text-emerald-600" onClick={() => setEditIdx(null)}>
                          <Check className="w-3.5 h-3.5 mr-1" /> OK
                        </Button>
                      </div>
                    ) : (
                      <>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate">{item.name}</p>
                          <div className="flex items-center gap-2 mt-0.5">
                            <span className="text-xs text-gray-400">{item.category}</span>
                            {item.description && <span className="text-xs text-gray-300 truncate max-w-[200px]">- {item.description}</span>}
                          </div>
                        </div>
                        <span className="text-sm font-bold text-gray-700 tabular-nums w-16 text-right flex-shrink-0">
                          {item.price > 0 ? `${item.price.toFixed(2)}€` : <span className="text-gray-300">0€</span>}
                        </span>
                        <button
                          className="w-7 h-7 rounded-full hover:bg-gray-100 flex items-center justify-center text-gray-400 hover:text-gray-600 flex-shrink-0 transition"
                          onClick={() => setEditIdx(idx)}
                          data-testid={`clone-edit-${idx}`}
                        >
                          <Pencil className="w-3.5 h-3.5" />
                        </button>
                      </>
                    )}
                  </div>
                ))}
              </div>
            </ScrollArea>

            {/* Footer */}
            <div className="px-6 py-3 border-t border-gray-100 flex items-center justify-between bg-white">
              <p className="text-xs text-gray-400">
                Edite nomes e precos antes de importar. Itens desmarcados nao serao importados.
              </p>
              <Button
                disabled={selectedCount === 0 || importing}
                className="bg-emerald-600 hover:bg-emerald-700 text-white gap-1.5"
                data-testid="clone-confirm-btn"
                onClick={handleConfirm}
              >
                {importing ? (
                  <><Loader2 className="w-4 h-4 animate-spin" /> Importando...</>
                ) : (
                  <><Check className="w-4 h-4" /> Importar {selectedCount} itens</>
                )}
              </Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
